import React from 'react';
import moment from 'moment';
import toast from 'react-hot-toast';
import { Oval } from 'react-loader-spinner';
import { BsChevronLeft, BsChevronRight, BsArrowRepeat } from 'react-icons/bs';

import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { fetchDriverSafetySummary, rescoreSafetyReport } from '../../driversSlice';
import { isAdminSelector } from '../../../Auth/authSlice';
import {
  SafetyReport,
  safetyReasonLabel,
  behaviorTypeLabel,
  speedingTypeLabel,
  windowBasisLabel,
} from '../../../../models';

import './SafetyTab.scss';

export interface SafetyTabProps {
  userId: string;
}

export function SafetyTab(props: SafetyTabProps) {
  const { userId } = props;
  const dispatch = useAppDispatch();

  const [offset, setOffset] = React.useState<number>(0);

  const data = useAppSelector(state => state.drivers.driverSafetySummary);
  const api = useAppSelector(state => state.drivers.getDriverSafetySummary);
  const isAdmin = useAppSelector(isAdminSelector);

  const [rescoring, setRescoring] = React.useState<Set<string>>(new Set());

  React.useEffect(() => {
    dispatch(fetchDriverSafetySummary({ driverId: userId, offset }));
  }, [dispatch, userId, offset]);

  const onRescore = (punchCardId: string) => {
    setRescoring(prev => new Set(prev).add(punchCardId));
    dispatch(rescoreSafetyReport(punchCardId))
      .unwrap()
      .then(() => {
        toast.success('Rescored from Samsara');
        return dispatch(fetchDriverSafetySummary({ driverId: userId, offset }));
      })
      .catch(() => toast.error('Could not rescore this shift'))
      .finally(() => {
        setRescoring(prev => {
          const next = new Set(prev);
          next.delete(punchCardId);
          return next;
        });
      });
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    if (mins === 0) return `${secs}s`;
    return `${mins}m ${secs}s`;
  };

  const formatMinutes = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours === 0) return `${mins} min`;
    if (mins === 0) return `${hours} hr`;
    return `${hours} hr ${mins} min`;
  };

  const formatImpact = (impact: number): string => impact.toFixed(2);

  const formatPeriodDates = (): string => {
    if (!data) return '';
    const start = moment(data.payPeriod.startDate);
    const end = moment(data.payPeriod.endDate);
    return `${start.format('MMM D')} – ${end.format('MMM D, YYYY')}`;
  };

  const hasPrev = !!data?.payPeriod.previousId;
  const hasNext = !!data?.payPeriod.nextId;

  const onPrev = () => { if (hasPrev) setOffset(o => o - 1); };
  const onNext = () => { if (hasNext) setOffset(o => o + 1); };

  const renderSummaryCard = () => {
    const s = data?.summary;
    return (
      <div className='summary-card'>
        <div className='summary-hero'>
          <span className='hero-value'>
            {s?.weightedScore != null ? s.weightedScore : 'No scored trips'}
          </span>
          <span className='hero-label'>Weighted safety score</span>
        </div>
        <div className='summary-stats'>
          <div className='summary-stat'>
            <span className='stat-value'>{s?.reportCount ?? 0}</span>
            <span className='stat-label'>Reports</span>
          </div>
          <div className='summary-stat'>
            <span className='stat-value'>{formatMinutes(s?.totalDriveMinutes ?? 0)}</span>
            <span className='stat-label'>Drive time</span>
          </div>
          <div className='summary-stat'>
            <span className='stat-value'>{(s?.totalDriveMiles ?? 0)} mi</span>
            <span className='stat-label'>Distance</span>
          </div>
        </div>
      </div>
    );
  };

  const renderBreakdown = (report: SafetyReport) => {
    if (report.behaviors.length === 0 && report.speeding.length === 0) {
      return <div className='no-events'>No events recorded.</div>;
    }
    return (
      <>
        {report.behaviors.length > 0 && (
          <div className='breakdown'>
            <div className='breakdown-title'>Behaviors</div>
            <div className='breakdown-row breakdown-header'>
              <span className='label'>Behavior</span>
              <span className='count'>Count</span>
              <span className='impact'>Score impact</span>
            </div>
            {report.behaviors.map((b, i) => (
              <div className='breakdown-row' key={`b-${i}`}>
                <span className='label'>{behaviorTypeLabel(b.behaviorType)}</span>
                <span className='count'>{b.count}</span>
                <span className='impact'>{formatImpact(b.scoreImpact)}</span>
              </div>
            ))}
          </div>
        )}
        {report.speeding.length > 0 && (
          <div className='breakdown'>
            <div className='breakdown-title'>Speeding</div>
            <div className='breakdown-row breakdown-header'>
              <span className='label'>Severity</span>
              <span className='count'>Duration</span>
              <span className='impact'>Score impact</span>
            </div>
            {report.speeding.map((s, i) => (
              <div className='breakdown-row' key={`s-${i}`}>
                <span className='label'>{speedingTypeLabel(s.speedingType)}</span>
                <span className='count'>{formatDuration(s.durationSeconds)}</span>
                <span className='impact'>{formatImpact(s.scoreImpact)}</span>
              </div>
            ))}
          </div>
        )}
      </>
    );
  };

  const renderRescoreButton = (report: SafetyReport) => {
    if (!isAdmin) return null;
    const busy = rescoring.has(report.punchCardId);
    return (
      <button
        className='rescore-button'
        onClick={() => onRescore(report.punchCardId)}
        disabled={busy}
        title="Re-fetch this shift's safety data from Samsara"
      >
        {busy ? (
          <Oval color="#131313" height={14} width={14} secondaryColor="#FBD20A" />
        ) : (
          <><BsArrowRepeat /> Rescore</>
        )}
      </button>
    );
  };

  const renderUnavailable = (report: SafetyReport, key: string) => (
    <div className='safety-card unavailable' key={key}>
      <div className='card-header'>
        <span className='window'>
          {moment(report.windowStart).format('ddd MMM D, h:mm A')} – {moment(report.windowEnd).format('h:mm A')}
        </span>
        {renderRescoreButton(report)}
      </div>
      <div className='unavailable-reason'>{safetyReasonLabel(report.reason || '')}</div>
    </div>
  );

  const renderAvailable = (report: SafetyReport, key: string) => (
    <div className='safety-card' key={key}>
      <div className='card-header'>
        <span className='window'>
          {moment(report.windowStart).format('ddd MMM D, h:mm A')} – {moment(report.windowEnd).format('h:mm A')}
        </span>
        <span className='basis'>{windowBasisLabel(report.windowEndBasis)}</span>
        {renderRescoreButton(report)}
      </div>

      <div className='stats-row'>
        <div className='stat score'>
          <span className='stat-value'>{report.vehicleScore ?? '—'}</span>
          <span className='stat-label'>Safety score</span>
        </div>
        <div className='stat'>
          <span className='stat-value'>
            {report.driveDistanceMiles != null ? `${report.driveDistanceMiles} mi` : '—'}
          </span>
          <span className='stat-label'>Distance</span>
        </div>
        <div className='stat'>
          <span className='stat-value'>
            {report.driveTimeMinutes != null ? formatMinutes(report.driveTimeMinutes) : '—'}
          </span>
          <span className='stat-label'>Drive time</span>
        </div>
      </div>

      {renderBreakdown(report)}
    </div>
  );

  const reports = data?.reports || [];

  return (
    <div className='safety-tab'>
      <div className='pay-period-picker'>
        <div
          className={`nav-button prev ${!hasPrev ? 'disabled' : ''}`}
          onClick={onPrev}
        >
          <BsChevronLeft />
        </div>
        <div className='pay-period-dates'>{formatPeriodDates()}</div>
        <div
          className={`nav-button next ${!hasNext ? 'disabled' : ''}`}
          onClick={onNext}
        >
          <BsChevronRight />
        </div>
        {api?.loading && (
          <div className='loading-indicator'>
            <Oval color="#131313" height={20} width={20} secondaryColor="#FBD20A" />
          </div>
        )}
      </div>

      {api?.error && !api?.loading && (
        <div className='error-state'>Couldn't load this pay period. Try another period.</div>
      )}

      {data && renderSummaryCard()}

      {!api?.loading && data && reports.length === 0 && (
        <div className='empty-state'>No shifts in this period.</div>
      )}

      {!api?.loading && reports.map((report, i) =>
        report.available
          ? renderAvailable(report, `${report.punchCardId}-${i}`)
          : renderUnavailable(report, `${report.punchCardId}-${i}`)
      )}
    </div>
  );
}
